/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * The input for WidgetSearchAccounts
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.4.0
 * @since 1.3.0
 */

import { ChangeEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../../../../assets';
import { Input } from '../../../../../../components/common';
import { LoaderCircleStyled } from '../../../../../../components/common/Loader/styled/LoadersStyled';
import IPageDashboardTranslation from '../../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { IWidgetSearchAccountsLoadListPayload } from '../types/widgetSearchAccountsActionTypes';
import { IWidgetSearchAccountsItem } from '../types/widgetSearchAccountsTypes';
import {
    widgetSearchAccountsLoadList,
    widgetSearchAccountsSetSearchValue,
} from '../widgetSearchAccountsActions';
import {
    WIDGET_SEARCH_ACCOUNTS_INPUT_DELAY,
    WIDGET_SEARCH_ACCOUNTS_MIN_SEARCH_LENGTH,
} from '../widgetSearchAccountsConstants';
import {
    makeSelectWidgetSearchAccountsHasMore,
    makeSelectWidgetSearchAccountsIsLoading,
    makeSelectWidgetSearchAccountsList,
    makeSelectWidgetSearchAccountsSearchValue,
} from '../widgetSearchAccountsSelectors';
import {
    WidgetSearchAccountsInputContainerStyled,
    WidgetSearchAccountsInputIconStyled,
    WidgetSearchAccountsLoaderContainerStyled,
} from './styled/WidgetSearchAccountsStyled';

interface IWidgetSearchAccountsInputProps {
    isOpened: boolean;
    onChange: () => void;
    translation: IPageDashboardTranslation;
}

interface IConnectedProps {
    accountList: IWidgetSearchAccountsItem[];
    hasMore: boolean;
    isLoading: boolean;
    searchValue: string;
}

interface IDispatchProps {
    loadAccountsList: (payload?: IWidgetSearchAccountsLoadListPayload) => void;
    setSearchValue: (payload: string) => void;
}

export type TWidgetSearchAccountsInputProps = IWidgetSearchAccountsInputProps & IConnectedProps & IDispatchProps;

export const WidgetSearchAccountsInput = (props: TWidgetSearchAccountsInputProps) => {
    const [localSearchValue, setLocalSearchValue] = useState<string>('');
    const inputRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        setLocalSearchValue(props.searchValue);
    }, [props.searchValue]);


    const startSearch = (searchValue: string) => {
        props.setSearchValue(searchValue);
        if (!searchValue || searchValue.length >= WIDGET_SEARCH_ACCOUNTS_MIN_SEARCH_LENGTH)
            props.loadAccountsList({ start: true });
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const searchValue = event.target.value ?? '';
        setLocalSearchValue(searchValue);
        delayedStartSearch(searchValue);
        props.onChange();
    };

    const delayedStartSearch = useCallback(debounce((searchValue: string) => {
        startSearch(searchValue);
    }, WIDGET_SEARCH_ACCOUNTS_INPUT_DELAY), []);

    const handleClearSearchValue = () => {
        setLocalSearchValue('');
        startSearch('');
        inputRef.current?.focus();
    };

    return (
        <WidgetSearchAccountsInputContainerStyled>
            <Input>
                <input
                    data-component="widgetInputToSearch"
                    placeholder={props.translation.widgets.searchAccounts.placeholder}
                    ref={inputRef}
                    value={localSearchValue}
                    onChange={handleChange}
                />
            </Input>
            {
                props.accountList.length > 0 && props.isLoading ?
                    <WidgetSearchAccountsLoaderContainerStyled
                        data-name="widgetLoaderCircle"
                    >
                        <LoaderCircleStyled/>
                    </WidgetSearchAccountsLoaderContainerStyled>
                    :
                    localSearchValue ?
                        <WidgetSearchAccountsInputIconStyled
                            cursor='pointer'
                            src={icons.cancelBlackIcon}
                            onClick={handleClearSearchValue}
                        />
                        :
                        <WidgetSearchAccountsInputIconStyled
                            src={icons.searchMediumIcon}
                        />
            }
        </WidgetSearchAccountsInputContainerStyled>
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountList: makeSelectWidgetSearchAccountsList(),
    hasMore: makeSelectWidgetSearchAccountsHasMore(),
    isLoading: makeSelectWidgetSearchAccountsIsLoading(),
    searchValue: makeSelectWidgetSearchAccountsSearchValue(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadAccountsList: (payload) =>
        dispatch(widgetSearchAccountsLoadList(payload)),
    setSearchValue: (payload) => dispatch(widgetSearchAccountsSetSearchValue({ searchValue: payload })),
});

export default connect(mapStateToProps, mapDispatchToProps)(WidgetSearchAccountsInput)
